import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCurrency, formatDate, formatDateTime, fileUrl } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { ArrowLeft, Phone, Mail, Calendar, Car, Check, X, Upload, FileText, Image as ImageIcon } from "lucide-react";

const DOC_LABELS = {
    license: "Driving License",
    rc: "RC Book",
    insurance: "Insurance",
    puc: "PUC Certificate",
    profile_photo: "Profile Photo",
    aadhaar: "Aadhaar",
    pan: "PAN Card",
    other: "Other",
};

const DocCard = ({ doc, onReview, onPreview }) => {
    const isImage = (doc.content_type || "").startsWith("image/");
    return (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`doc-card-${doc.id}`}>
            <button onClick={() => onPreview(doc)} className="w-full h-40 bg-slate-50 flex items-center justify-center relative">
                {isImage ? (
                    <img src={fileUrl(doc.file_url)} alt={doc.type} className="w-full h-full object-cover" />
                ) : (
                    <div className="text-slate-400 flex flex-col items-center gap-1">
                        <FileText className="w-8 h-8" />
                        <span className="text-xs">{(doc.content_type || "file").split("/").pop().toUpperCase()}</span>
                    </div>
                )}
                <div className="absolute top-2 right-2">
                    <StatusBadge status={doc.status} />
                </div>
            </button>
            <div className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-navy-900">{DOC_LABELS[doc.type] || doc.type}</div>
                </div>
                <div className="text-xs text-slate-500">Uploaded {formatDateTime(doc.uploaded_at)}</div>
                {doc.status === "rejected" && doc.rejection_reason && (
                    <div className="text-xs text-red-600 italic">"{doc.rejection_reason}"</div>
                )}
                {doc.status === "pending" && (
                    <div className="flex gap-1.5 pt-1">
                        <button
                            onClick={() => onReview(doc, "approve")}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-mint-500 hover:bg-mint-600 text-white text-xs font-semibold"
                            data-testid={`doc-approve-${doc.id}`}
                        >
                            <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                            onClick={() => onReview(doc, "reject")}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold"
                            data-testid={`doc-reject-${doc.id}`}
                        >
                            <X className="w-3.5 h-3.5" /> Reject
                        </button>
                    </div>
                )}
                {doc.status !== "pending" && (
                    <div className="text-[11px] text-slate-400">Reviewed by {doc.reviewed_by || "—"}</div>
                )}
            </div>
        </div>
    );
};

const DriverDetail = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [actLoading, setActLoading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [uploadType, setUploadType] = useState("license");
    const [uploading, setUploading] = useState(false);

    const load = () => api.get(`/drivers/${id}`).then((r) => setData(r.data));
    useEffect(() => { load(); }, [id]);

    if (!data) return <div className="text-slate-500">Loading…</div>;
    const { driver, rides } = data;
    const documents = driver.documents || [];

    const handleKyc = async (action) => {
        setActLoading(true);
        try {
            await api.post(`/drivers/${id}/kyc`, { action });
            await load();
        } finally { setActLoading(false); }
    };

    const reviewDoc = async (doc, action) => {
        let reason = null;
        if (action === "reject") {
            reason = window.prompt("Reason for rejection (will be shown to the driver):");
            if (!reason) return;
        }
        await api.post(`/drivers/${id}/documents/${doc.id}/review`, { action, reason });
        load();
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("type", uploadType);
            fd.append("file", file);
            await api.post(`/public/drivers/${id}/documents/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await load();
        } catch (err) {
            window.alert("Upload failed: " + (err.response?.data?.detail || err.message));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    return (
        <div className="space-y-6">
            <Link to="/drivers" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-mint-600" data-testid="driver-back-link">
                <ArrowLeft className="w-4 h-4" /> Back to drivers
            </Link>

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-navy-700 to-navy-900 text-white flex items-center justify-center font-display font-bold text-3xl">
                        {driver.name?.[0] || "D"}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="font-display font-bold text-2xl text-navy-900" data-testid="driver-name">{driver.name}</h1>
                            <StatusBadge status={driver.kyc_status} />
                            {driver.online && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-mint-700 bg-mint-50 px-2 py-0.5 rounded-full border border-mint-200"><span className="w-1.5 h-1.5 rounded-full bg-mint-500" /> Online</span>}
                        </div>
                        <div className="text-slate-500 mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                            <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{driver.phone}</span>
                            <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{driver.email}</span>
                            <span className="flex items-center gap-1.5"><Car className="w-3.5 h-3.5" />{driver.vehicle_type} · {driver.vehicle_number}</span>
                            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Joined {formatDate(driver.created_at)}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">License: <span className="font-mono">{driver.license_no}</span></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 w-full md:w-auto">
                        <div className="text-center"><div className="text-xs text-slate-500 uppercase tracking-wider">Rides</div><div className="font-display font-bold text-navy-900 text-2xl">{driver.total_rides || 0}</div></div>
                        <div className="text-center"><div className="text-xs text-slate-500 uppercase tracking-wider">Earnings</div><div className="font-display font-bold text-navy-900 text-2xl">{formatCurrency(driver.total_earnings || 0)}</div></div>
                        <div className="text-center"><div className="text-xs text-slate-500 uppercase tracking-wider">Rating</div><div className="font-display font-bold text-navy-900 text-2xl">{driver.rating} ★</div></div>
                    </div>
                </div>

                {driver.kyc_status !== "approved" && (
                    <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap gap-2">
                        <button onClick={() => handleKyc("approve")} disabled={actLoading} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-mint-500 hover:bg-mint-600 text-white text-sm font-semibold disabled:opacity-60" data-testid="driver-kyc-approve">
                            <Check className="w-4 h-4" /> Approve KYC (all docs)
                        </button>
                        <button onClick={() => handleKyc("reject")} disabled={actLoading} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold disabled:opacity-60" data-testid="driver-kyc-reject">
                            <X className="w-4 h-4" /> Reject KYC
                        </button>
                    </div>
                )}
            </div>

            {/* Documents */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <div className="font-display font-bold text-navy-900 text-lg">Documents</div>
                        <div className="text-xs text-slate-500">Review each document independently. KYC auto-approves when all documents are approved.</div>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} className="text-xs px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg" data-testid="doc-upload-type">
                            {Object.entries(DOC_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-mint-200 text-mint-700 hover:bg-mint-50 text-xs font-semibold">
                            <Upload className="w-3.5 h-3.5" />
                            {uploading ? "Uploading…" : "Upload doc"}
                            <input type="file" className="hidden" onChange={handleUpload} accept="image/*,.pdf" data-testid="doc-upload-input" />
                        </span>
                    </label>
                </div>
                {documents.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
                        <ImageIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        No documents uploaded yet. Driver app can upload via{" "}
                        <span className="font-mono text-mint-700">POST /api/public/drivers/{driver.id}/documents/upload</span>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {documents.map((d) => (
                            <DocCard key={d.id} doc={d} onReview={reviewDoc} onPreview={setPreview} />
                        ))}
                    </div>
                )}
            </div>

            {/* Recent rides */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 font-display font-semibold text-navy-900">Recent rides</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                <th className="px-4 py-3">Ride ID</th>
                                <th className="px-4 py-3">Customer</th>
                                <th className="px-4 py-3">Route</th>
                                <th className="px-4 py-3 text-right">Fare</th>
                                <th className="px-4 py-3 text-right">Payout</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rides.length === 0 ? (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No rides yet.</td></tr>
                            ) : rides.map((r) => (
                                <tr key={r.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3"><Link to={`/rides/${r.id}`} className="text-mint-600 font-semibold hover:underline">{r.id}</Link></td>
                                    <td className="px-4 py-3">{r.customer?.name || "—"}</td>
                                    <td className="px-4 py-3 text-slate-600 max-w-[300px] truncate">{r.pickup_address} → {r.drop_address}</td>
                                    <td className="px-4 py-3 text-right">{formatCurrency(r.fare)}</td>
                                    <td className="px-4 py-3 text-right font-semibold text-mint-700">{formatCurrency(r.driver_payout)}</td>
                                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                                    <td className="px-4 py-3 text-slate-500">{formatDateTime(r.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Preview Modal */}
            {preview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setPreview(null)}>
                    <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setPreview(null)} className="absolute -top-10 right-0 text-white" data-testid="doc-preview-close"><X className="w-6 h-6" /></button>
                        {(preview.content_type || "").startsWith("image/") ? (
                            <img src={fileUrl(preview.file_url)} alt={preview.type} className="max-w-full max-h-[85vh] rounded-2xl" />
                        ) : (
                            <iframe src={fileUrl(preview.file_url)} title="doc" className="w-[80vw] h-[85vh] rounded-2xl bg-white" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DriverDetail;
